import Select from '@components/v2/form/Select/Select';
import TextField from '@components/v2/form/TextField/TextField';
import Modal from '@components/v2/ui/Modal/Modal';
import TypographyV2 from '@components/v2/ui/Typography/Typography';
import { Box, MenuItem } from '@mui/material';
import LangEnum from '../../../../../../../../../../lang/LangEnum';
import { RachelFeedbackProblemType } from '../../../../../../../../../../services/modules/rachelChats/rachelChats.types';
import { useTranslation } from '../../../../../../../../../translation/TranslationContext';
import { RACHEL_FEEDBACK_PROBLEM_TYPES } from './rachelMessageFeedbackModal.consts';
import {
  RachelFeedbackSubmitParams,
  useRachelMessageFeedbackModal,
} from './useRachelMessageFeedbackModal';

const COMMENT_MIN_ROWS = 3;

const COMMENT_MAX_ROWS = 5;

interface RachelMessageFeedbackModalProps {
  isOpen: boolean;
  rating: 'like' | 'dislike';
  initialComment: string | null;
  initialProblemType: RachelFeedbackProblemType | null;
  onClose: () => void;
  onSubmit: (params: RachelFeedbackSubmitParams) => void;
}

const RachelMessageFeedbackModal = ({
  isOpen,
  rating,
  initialComment,
  initialProblemType,
  onClose,
  onSubmit,
}: RachelMessageFeedbackModalProps) => {
  const { translate } = useTranslation();
  const {
    comment,
    setComment,
    problemType,
    setProblemType,
    isDiscardConfirmOpen,
    handleSubmit,
    requestClose,
    keepEditing,
    discardChanges,
    handleExited,
  } = useRachelMessageFeedbackModal({ initialComment, initialProblemType, onSubmit, onClose });

  const isDislike = rating === 'dislike';

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="standard"
        onClose={requestClose}
        onExited={handleExited}
        header={{ title: translate(LangEnum.RACHEL_FEEDBACK_MODAL_TITLE) }}
        footer={{
          primaryAction: {
            label: LangEnum.RACHEL_INPUT_SEND,
            onClick: handleSubmit,
          },
          secondaryAction: {
            label: LangEnum.RACHEL_CANCEL_ACTION,
            onClick: requestClose,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {isDislike && (
            <Select
              fullWidth
              label={translate(LangEnum.RACHEL_FEEDBACK_PROBLEM_TYPE_LABEL)}
              value={problemType}
              onChange={(event) => setProblemType(event.target.value as RachelFeedbackProblemType)}
            >
              {RACHEL_FEEDBACK_PROBLEM_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {translate(option.label)}
                </MenuItem>
              ))}
            </Select>
          )}
          <TextField
            multiline
            minRows={COMMENT_MIN_ROWS}
            maxRows={COMMENT_MAX_ROWS}
            label={translate(LangEnum.RACHEL_FEEDBACK_COMMENT_LABEL)}
            placeholder={translate(LangEnum.RACHEL_FEEDBACK_COMMENT_LABEL)}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <TypographyV2 variant="micro2" color="text.disabled">
            {translate(LangEnum.RACHEL_FEEDBACK_HELPER_TEXT)}
            <TypographyV2
              component="span"
              variant="micro2"
              color="text.secondary"
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {translate(LangEnum.RACHEL_FEEDBACK_LEARN_MORE)}
            </TypographyV2>
          </TypographyV2>
        </Box>
      </Modal>

      <Modal
        isOpen={isDiscardConfirmOpen}
        size="compact"
        onClose={keepEditing}
        header={{ title: LangEnum.GLOBAL_DISCARD_CHANGES_TITLE }}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_DISCARD,
            onClick: discardChanges,
          },
          secondaryAction: {
            label: LangEnum.GLOBAL_CANCEL_LABEL,
            onClick: keepEditing,
          },
        }}
      >
        <TypographyV2 variant="body2" color="text.secondary">
          {translate(LangEnum.GLOBAL_DISCARD_CHANGES_DESCRIPTION)}
        </TypographyV2>
      </Modal>
    </>
  );
};

export default RachelMessageFeedbackModal;
